import React from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Popover,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  IconButton,
  Badge,
  List,
} from '@mui/material';
import {
  ExitToApp,
  KeyboardArrowDown,
  PersonAdd,
  SwitchAccount,
  Notifications as NotificationsIcon,
  Apps as AppsIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import NotificationItem from './NotificationItem';
import vyontaLogoInk from '../../assets/Vyonta-logo-ink.png';

const colors = {
  ink: "#06185f",
  muted: "#4c5d86",
  blue: "#0059f6",
  line: "#d9e3f4",
};

// ANIMACIÓN (EFECTO CAMPANA)
const bellAnimation = {
  '@keyframes ring': {
    '0%': { transform: 'rotate(0)' },
    '10%': { transform: 'rotate(25deg)' },
    '20%': { transform: 'rotate(-25deg)' },
    '30%': { transform: 'rotate(15deg)' },
    '40%': { transform: 'rotate(-15deg)' },
    '50%': { transform: 'rotate(10deg)' },
    '60%': { transform: 'rotate(-10deg)' },
    '70%': { transform: 'rotate(0)' },
    '100%': { transform: 'rotate(0)' }, // Pausa al final
  },
};
// Helper para iniciales
const getInitials = (name) => {
  if (!name) return "";
  const parts = name.split(" ");
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};


export default function HeaderUI({
  empresaNombre,
  profileName,
  rol,
  anchorEl,
  open,
  onMenuClick,
  onMenuClose,
  onLogout,
  onSwitchProfile,
  onCrearClienteClick,
  notifAnchorEl,
  notifOpen,
  onNotifClick,
  onNotifClose,
  notifications = [],
  unreadCount = 0,
  onNotificationItemClick,
  onResolveNotification,
  onMarkNotificationRead,
  onViewNotificationClient,
  onDeleteNotification,
  moduleTitle = "VENTAS",
  showNotifications = true,
  onBackToMenu,
  onLogoClick,
  hideUserInfoInMenu = false,
}) {

  const handleCrearCliente = () => {
    onMenuClose();
    onCrearClienteClick();
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: 60,
        px: { xs: 2, sm: 4 },
        boxSizing: "border-box",
        backgroundColor: "rgba(255,255,255,0.94)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: "0 0 10px 10px",
        boxShadow: "0 8px 22px rgba(15, 42, 89, 0.12)",
        position: "sticky",
        top: 0,
        zIndex: 1100,
        color: colors.ink,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.4, sm: 2 }, minWidth: 0, flexShrink: 0 }}>
        <Box
          onClick={onLogoClick}
          sx={{
            width: 96,
            height: 38,
            position: "relative",
            overflow: "hidden",
            flexShrink: 0,
            cursor: onLogoClick ? 'pointer' : 'default',
          }}
        >
          <Box
            component="img"
            src={vyontaLogoInk}
            alt="Vyonta"
            sx={{
              position: "absolute",
              width: 164,
              height: "auto",
              left: -33,
              top: -29,
              display: "block",
            }}
          />
        </Box>

        <Box
          sx={{
            width: "1px",
            height: 30,
            backgroundColor: colors.line,
            display: { xs: "none", sm: "block" },
            flexShrink: 0,
          }}
        />

        <Typography
          sx={{
            color: colors.blue,
            letterSpacing: 0.4,
            fontWeight: 900,
            fontSize: { xs: 14, sm: 16 },
            textTransform: 'uppercase',
            whiteSpace: "nowrap",
          }}
        >
          {moduleTitle}
        </Typography>
      </Box>

      {/* DERECHA: Usuario (Menú Unificado) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1.4 } }}>
        {/* ICONO DE NOTIFICACIONES */}
        {showNotifications && (
          <>
            <IconButton
              onClick={onNotifClick}
              sx={{
                width: { xs: 34, sm: 38 },
                height: { xs: 34, sm: 38 },
                color: colors.ink,
                '&:hover': { backgroundColor: "#f0f6ff" },
              }}
            >
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon
                  sx={{
                    ...bellAnimation,
                    animation: unreadCount > 0 ? 'ring 2.5s ease-in-out infinite' : 'none',
                    transformOrigin: 'top center'
                  }}
                />
              </Badge>
            </IconButton>
            {/* MENÚ DE NOTIFICACIONES */}
            <Popover
              open={notifOpen}
              anchorEl={notifAnchorEl}
              onClose={onNotifClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 1.5,
                  width: 360,
                  maxHeight: 480,
                  borderRadius: "10px",
                  border: "1px solid #dce5f2",
                  boxShadow: "0 18px 38px rgba(19, 39, 84, 0.14)",
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }
              }}
            >
              <Box sx={{ p: 2, borderBottom: `1px solid ${colors.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, bgcolor: 'white', zIndex: 3 }}>
                <Typography variant="subtitle1" sx={{ color: colors.ink, fontWeight: 900 }}>Notificaciones</Typography>
                {unreadCount > 0 && <Typography variant="caption" sx={{ color: colors.blue, fontWeight: 800 }}>{unreadCount} nuevas</Typography>}
              </Box>

              <List sx={{ p: 0, overflowY: 'auto', flex: 1, maxHeight: '100%' }}>
                {notifications.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">No tienes notificaciones pendientes</Typography>
                  </Box>
                ) : (
                  notifications.map((notif) => (
                    <NotificationItem
                      key={notif.id}
                      notif={notif}
                      rol={rol}
                      onMainClick={onNotificationItemClick}
                      onResolve={onResolveNotification}
                      onMarkRead={onMarkNotificationRead}
                      onViewCliente={onViewNotificationClient}
                      onDelete={onDeleteNotification}
                    />
                  ))
                )}
              </List>
            </Popover>
          </>
        )}
        {profileName && (
          <>
            <Box
              onClick={onMenuClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.8, sm: 1.4 },
                cursor: 'pointer',
                py: 0.5,
                pl: { xs: 0.5, sm: 1.2 },
                pr: 0.3,
                borderRadius: 999,
                transition: 'background-color 160ms ease',
                '&:hover': { backgroundColor: '#f0f6ff' }
              }}
            >
              <Typography
                sx={{
                  color: colors.ink,
                  fontSize: { xs: 11.5, sm: 13 },
                  fontWeight: 800,
                  display: { xs: 'none', sm: 'block' },
                  maxWidth: 180,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {empresaNombre}
              </Typography>

              <Avatar
                sx={{
                  width: { xs: 34, sm: 38 },
                  height: { xs: 34, sm: 38 },
                  color: colors.blue,
                  backgroundColor: "#f5f9ff",
                  border: "1px solid #bed3ff",
                  boxShadow: "0 4px 12px rgba(0, 89, 246, 0.10)",
                  fontSize: 13,
                  fontWeight: 900,
                }}
              >
                {hideUserInfoInMenu ? <PersonIcon sx={{ fontSize: 20 }} /> : getInitials(profileName)}
              </Avatar>

              <KeyboardArrowDown sx={{ color: colors.blue, fontSize: 20 }} />
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={onMenuClose}
              onClick={onMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 1,
                  minWidth: 240,
                  borderRadius: "10px",
                  border: "1px solid #dce5f2",
                  boxShadow: "0 16px 32px rgba(19, 39, 84, 0.14)",
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {/* --- INFO DEL USUARIO --- */}
                {!hideUserInfoInMenu && (
                  <>
                    <Box sx={{ px: 2, py: 1.5, outline: 'none' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 900, color: colors.ink }}>
                        {profileName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.muted, textTransform: 'capitalize' }}>
                        {rol === "Admin" ? 'Supervisor' : 'Vendedor'}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                  </>
                )}

              {/* --- ACCIONES DE VENDEDOR --- */}
              {rol === 'Vendedor' && (
                <>
                  <MenuItem onClick={handleCrearCliente}>
                    <ListItemIcon>
                      <PersonAdd fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText>Crear lead</ListItemText>
                  </MenuItem>
                  <Divider sx={{ my: 1 }} />
                </>
              )}

              {/* --- OPCIONES DE SESIÓN --- */}
              {onBackToMenu ? (
                // Opción para Admin: Volver al menú principal
                <MenuItem onClick={onBackToMenu}>
                  <ListItemIcon>
                    <AppsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Volver al Menú</ListItemText>
                </MenuItem>
              ) : (
                // Opción para Ventas: Cambiar Usuario
                <MenuItem onClick={onSwitchProfile}>
                  <ListItemIcon>
                    <SwitchAccount fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Cambiar Usuario</ListItemText>
                </MenuItem>
              )}

              <MenuItem onClick={onLogout} sx={{ color: 'error.main' }}>
                <ListItemIcon>
                  <ExitToApp fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Cerrar Sesión</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
}
